import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import {
  artworkColumnsFormatted,
  artworkColumnsVisibility,
} from "@/config/dashboard/artwork";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import { Artwork } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RelatedRecordRowActions } from "@/components/table/RelatedRecordRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import { ArtworkLinkGridCard } from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";

const STORAGE_KEY = "artwork-links-table-preferences";

interface ArtworkLinksProps {
  id: string;
  type: LinkableType;
}

/**
 * ArtworkLinks component
 * @returns {JSX.Element}
 */
const ArtworkLinks = ({ id, type }: ArtworkLinksProps) => {
  const { t } = useTranslation();

  const [getPaginationRelatedArtworks, setPaginationRelatedArtworks] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: artworksRelated } = useGetRelatedFields(
    id,
    type,
    "relatedArtwork",
    getPaginationRelatedArtworks.pageSize,
    getPaginationRelatedArtworks.page * getPaginationRelatedArtworks.pageSize,
  );

  const { data: countRelatedArtworks } = useCountRelatedFields(id, type, [
    "relatedArtwork",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedArtwork",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedArtwork",
  );

  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedArtworkId, setSelectedArtworkId] = useState<string | null>(null);
  const [searchContent, setSearchContent] = useState("");

  const [getPaginationArtworksToAdd, setPaginationArtworksToAdd] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const { data: notAlreadyRelatedArtworks } = useGetNotRelatedResource<Artwork>(
    {
      resultType: "artwork",
      resourceId: id,
      pagination: getPaginationArtworksToAdd,
      searchQuery: searchContent,
    },
  );

  const handleCreateRelation = useCallback(
    (artworkId: string) => {
      try {
        createRelatedFields([id, artworkId]);
      } catch (error) {
        console.error(error);
      }
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (artworkId: string) => {
      deleteRelation(artworkId);
    },
    [deleteRelation],
  );

  const renderArtworkRowActions = useCallback(
    (artwork: Artwork) => (
      <RelatedRecordRowActions
        isAddMode={isAddMode}
        resourceId={artwork.id}
        previewType="artwork"
        previewElements={[
          {
            label: t("oeuvresDescriptionTitre"),
            value: artwork.oeuvresDescriptionTitre,
            type: "text",
          },
          {
            label: t("oeuvresDescriptionNumeroEnregistrement"),
            value: artwork.oeuvresDescriptionNumeroEnregistrement,
            type: "text",
          },
          {
            label: t("oeuvresDescriptionResume"),
            value: artwork.oeuvresDescriptionResume,
            type: "richtext",
          },
          {
            label: t("attachments"),
            value: artwork.attachments,
            type: "image",
          },
        ]}
        onDelete={() => handleDeleteRelation(artwork.id)}
        onAdd={() => handleCreateRelation(artwork.id)}
      />
    ),
    [handleCreateRelation, handleDeleteRelation, isAddMode, t],
  );

  const columns: ColumnDef<Artwork, any>[] = useMemo(() => {
    return [
      ...generateTable(artworkColumnsFormatted, "artwork"),
      createActionsColumnDef<Artwork>(({ row }) =>
        renderArtworkRowActions(row.original),
      ),
    ];
  }, [renderArtworkRowActions]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { visibility } = saved;
        return (
          visibility || {
            ...artworkColumnsVisibility,
            oeuvresDescriptionVignette: true,
            oeuvresPublicationStatus: false,
          }
        );
      }
      return {
        ...artworkColumnsVisibility,
        oeuvresDescriptionVignette: true,
        oeuvresPublicationStatus: false,
      };
    },
  );

  const flatVisibleArtworkColumns = artworkColumnsFormatted.flatMap(
    (column: ColumnFormatted) => (column.visible ? column.key : []),
  );

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = readTablePreferences(STORAGE_KEY);
    if (saved) {
      const { order } = saved;
      return order || flatVisibleArtworkColumns;
    }
    return flatVisibleArtworkColumns;
  });

  const tableOptions = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnsOrder,
    state: {
      columnVisibility: columnVisibility,
      columnOrder: columnsOrder,
    },
  };

  const relatedPaginationProps = {
    pagination: getPaginationRelatedArtworks,
    setPage: setPaginationRelatedArtworks,
    totalSize: countRelatedArtworks?.relatedArtwork ?? 0,
  };

  return (
    <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY}
      title={t("linkedArtworks")}
      isAddMode={isAddMode}
      onBack={handleAddMode}
      onToggleAdd={handleAddMode}
      searchInput={
        <div className="flex items-center gap-2">
          <Input
            placeholder={t("search")}
            className="w-80"
            value={searchContent}
            onChange={(e) => setSearchContent(e.target.value)}
          />
        </div>
      }
      addModeTable={
        <DataTable
          columns={columns}
          data={notAlreadyRelatedArtworks?.data ?? []}
          pagination={{
            pagination: getPaginationArtworksToAdd,
            setPage: setPaginationArtworksToAdd,
            totalSize: notAlreadyRelatedArtworks?.totalSize ?? 0,
          }}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      listTable={
        <DataTable
          columns={columns}
          data={artworksRelated ?? []}
          pagination={relatedPaginationProps}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      gridContent={
        <>
          {artworksRelated?.map((artwork: Artwork) => (
            <div key={artwork.id}>
              <GridCardWithActions actions={renderArtworkRowActions(artwork)}>
                <ArtworkLinkGridCard
                  artwork={artwork}
                  t={t}
                  onTextClick={() => setSelectedArtworkId(artwork.id)}
                />
              </GridCardWithActions>
              <ShowElements
                type="artwork"
                id={artwork.id}
                open={selectedArtworkId === artwork.id}
                onOpenChange={(open) => {
                  if (!open) {
                    setSelectedArtworkId(null);
                  }
                }}
                trigger={<span />}
                elements={[
                  {
                    label: t("oeuvresDescriptionTitre"),
                    value: artwork.oeuvresDescriptionTitre,
                    type: "text",
                  },
                  {
                    label: t("oeuvresDescriptionNumeroEnregistrement"),
                    value: artwork.oeuvresDescriptionNumeroEnregistrement,
                    type: "text",
                  },
                  {
                    label: t("oeuvresDescriptionResume"),
                    value: artwork.oeuvresDescriptionResume,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: artwork.attachments,
                    type: "image",
                  },
                ]}
              />
            </div>
          ))}
        </>
      }
      relatedPagination={relatedPaginationProps}
    />
  );
};

export default ArtworkLinks;
